import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardHeader, CardTitle, Input } from '@monigo/ui';
import { ProfileSchema, type ProfileInput } from '@monigo/shared';
import { useSaveProfile } from './api.js';

export function ProfileSetupPage() {
  const { t, i18n } = useTranslation();
  const save = useSaveProfile();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProfileInput>({
    resolver: zodResolver(ProfileSchema),
    defaultValues: { language: i18n.language === 'en' ? 'en' : 'fr' },
  });

  return (
    <div className="container max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.setupTitle')}</CardTitle>
        </CardHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={handleSubmit(async (values) => {
            await save.mutateAsync(values);
            navigate('/app');
          })}
        >
          <Field label={t('profile.firstName')} error={errors.first_name?.message}>
            <Input hasError={!!errors.first_name} {...register('first_name')} />
          </Field>
          <Field label={t('profile.lastName')} error={errors.last_name?.message}>
            <Input hasError={!!errors.last_name} {...register('last_name')} />
          </Field>
          <Field label={t('profile.dob')} error={errors.dob?.message}>
            <Input type="date" hasError={!!errors.dob} {...register('dob')} />
          </Field>
          <Field label={t('profile.address')} error={errors.address?.message}>
            <Input hasError={!!errors.address} {...register('address')} />
          </Field>
          <Field label={t('profile.country')} error={errors.country?.message}>
            <Input maxLength={2} hasError={!!errors.country} {...register('country')} />
          </Field>
          <Field label={t('profile.language')} error={errors.language?.message}>
            <select
              className="h-11 w-full rounded-md border border-border bg-surface px-3 text-text"
              {...register('language')}
            >
              <option value="fr">{t('profile.languageFr')}</option>
              <option value="en">{t('profile.languageEn')}</option>
            </select>
          </Field>
          {save.isError ? <p className="text-sm text-danger">{t('errors.generic')}</p> : null}
          <Button type="submit" isLoading={isSubmitting || save.isPending}>
            {t('profile.saveCta')}
          </Button>
        </form>
      </Card>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-text-muted">{label}</span>
      {children}
      {error ? <span className="text-xs text-danger">{error}</span> : null}
    </label>
  );
}
